import { Action, Reducer } from 'redux';

import { STATUS } from 'pages/views/devices/socketClient/types';
import { Printer } from 'pages/views/devices/types';
import { getHubSmartState } from 'pages/views/devices/utils/storage';
import { createReducer } from '@reduxjs/toolkit';

interface ActionWithPayload<Type, Payload> extends Action<Type> {
  payload: Payload;
}

type ActionCreator<Type, Payload> = (payload: Payload) => ActionWithPayload<Type, Payload>;

export interface HubState {
  connection: { ably: STATUS; hub: STATUS; browserCFD: STATUS };
  defaultPrinters: {
    RECEIPT?: Printer;
    LABEL?: Printer;
  };
  selectedDisplay:
    | {
        id?: number;
      }
    | Record<string, never>;
  version: string | null;
  hasStartedHubDownload: boolean;
  cfdModal: string;
}

export const initialState: HubState = {
  connection: {
    ably: STATUS.DISCONNECTED,
    hub: STATUS.DISCONNECTED,
    browserCFD: STATUS.DISCONNECTED,
  },
  defaultPrinters: {},
  selectedDisplay: {},
  version: null,
  hasStartedHubDownload: false,
  cfdModal: '',
};

type HUB_ACTIONS =
  | 'ABLY_UPDATE_CONNECTION_STATUS'
  | 'HUB_UPDATE_CONNECTION_STATUS'
  | 'HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS'
  | 'HUB_UPDATE_DEVICES'
  | 'HUB_UPDATE_DEFAULT_PRINTERS'
  | 'HUB_CLEAR_DEVICES'
  | 'HUB_UPDATE_CFD_MODAL'
  | 'HUB_UPDATE_DOWNLOAD_STATUS'
  | 'HUB_PRINT_TIMEOUT'
  | 'HUB_CLEAR_STORE';

export const updateConnectionStatus = (payload: STATUS) => ({
  type: 'ABLY_UPDATE_CONNECTION_STATUS',
  payload,
});
export const updateHubConnectionStatus = (payload: STATUS) => ({
  type: 'HUB_UPDATE_CONNECTION_STATUS',
  payload,
});

export const updateBrowserCFDConnectionStatus: ActionCreator<HUB_ACTIONS, STATUS> = payload => ({
  type: 'HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS',
  payload,
});

export const updateDevices: ActionCreator<
  HUB_ACTIONS,
  {
    version?: string;
    defaultPrinters?: HubState['defaultPrinters'];
    selectedDisplay?: HubState['selectedDisplay'];
  }
> = payload => ({
  type: 'HUB_UPDATE_DEVICES',
  payload,
});

export const clearDevices = (): Action<HUB_ACTIONS> => ({
  type: 'HUB_CLEAR_DEVICES',
});
export const clearHubStore = (): Action<HUB_ACTIONS> => ({
  type: 'HUB_CLEAR_STORE',
});

export const updateDefaultPrinters: ActionCreator<
  HUB_ACTIONS,
  Partial<HubState['defaultPrinters']>
> = payload => ({
  type: 'HUB_UPDATE_DEFAULT_PRINTERS',
  payload,
});

const createHubReducer = (): Reducer<HubState, Action<HUB_ACTIONS> & { payload: any }> =>
  createReducer(getHubSmartState() || initialState, {
    ABLY_UPDATE_CONNECTION_STATUS: (state, action) => {
      state.connection.ably = action.payload;
      if (action.payload === STATUS.DISCONNECTED) {
        state.connection.hub = STATUS.DISCONNECTED;
        state.connection.browserCFD = STATUS.DISCONNECTED;
        state.defaultPrinters = {};
      }
    },
    HUB_UPDATE_DEFAULT_PRINTERS: (state, action) => {
      return {
        ...state,
        defaultPrinters: { ...state.defaultPrinters, ...action.payload },
      };
    },
    HUB_UPDATE_CONNECTION_STATUS: (state, action) => {
      const status = action.payload as STATUS;

      if (status !== STATUS.CONNECTED) {
        state.defaultPrinters = {};
      }

      state.connection.hub = status;
    },
    HUB_UPDATE_BROWSER_CFD_CONNECTION_STATUS: (state, action) => {
      return {
        ...state,
        connection: { ...state.connection, browserCFD: action.payload },
      };
    },
    HUB_CLEAR_DEVICES: state => {
      return {
        ...state,
        defaultPrinters: {},
        selectedDisplay: {},
        version: null,
      };
    },
    HUB_UPDATE_DEVICES: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_UPDATE_CFD_MODAL: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_UPDATE_DOWNLOAD_STATUS: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_PRINT_TIMEOUT: (state, action) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    HUB_CLEAR_STORE: () => {
      return initialState;
    },
  });

export default createHubReducer;
